import { createContext, useContext, useState, useMemo, useCallback } from 'react';

const translations = {
  it: {
    nav: {
      stay: 'Gli Spazi',
      gallery: 'Galleria',
      experiences: 'Esperienze',
      location: 'Posizione',
      book: 'Prenota',
    },
    hero: {
      eyebrow: 'La Caletta · Sardegna',
      title_a: 'La Caletta',
      title_b: "all'Alba",
      sub: 'Appartamento vista mare sulla costa orientale della Sardegna.',
      cta: "Scopri l'appartamento",
    },
    intro: {
      eyebrow: 'Gli Spazi',
      title: 'Luce, mare\ne semplicità.',
      body: "Ambienti luminosi, dettagli essenziali e una terrazza vista mare pensata per vivere la Sardegna con calma, tra giornate di sole e serate all'aperto.",
      details: 'Vista mare · 5 ospiti · 2 bagni · terrazza privata · costa orientale',
      guests: 'ospiti',
      bedrooms: 'camere',
      bathrooms: 'bagni',
      terrace: 'terrazza',
    },
    gallery: {
      eyebrow: 'Galleria',
      title: 'Gallery',
      sub: "Scopri ogni dettaglio dell'appartamento, pensato per il comfort, il relax e un soggiorno perfetto in Sardegna.",
      captions: ['Vista sulla Baia','Living e Cucina Open Space','Zona Living','Cucina Attrezzata','Sapori di Sardegna','Divano Letto','Terrazza','Dettagli Mediterranei','Camera Matrimoniale','Camera Matrimoniale','Bagno in Camera','Camera Doppia','Bagno Moderno','Dettagli in Pietra','Ingresso Privato & Cortile'],
      coming_soon: 'In arrivo',
    },
    services: {
      eyebrow: 'Servizi',
      title: 'Tutto il necessario,\nnulla di superfluo.',
      list: [
        'Vista mare',
        'Aria condizionata',
        'WiFi veloce',
        'Cucina attrezzata',
        'Smart TV',
        'Terrazza privata',
        'Parcheggio gratuito',
        'Check-in flessibile',
        'Angolo caffè',
        'Biancheria inclusa',
        'Lavatrice',
        'Cortile privato con doccia esterna',
      ],
    },
    lifestyle: {
      eyebrow: 'Destinazione',
      title: 'La costa orientale,\nautentica.',
      body: "Acque trasparenti, calette nascoste e l'aria salmastra della Sardegna autentica. A pochi passi spiagge bianche, sentieri profumati di macchia e quel silenzio mediterraneo che si trova solo qui.",
      quote: 'Il mare in Sardegna è un linguaggio: chi ascolta, capisce.',
    },
    experiences: {
      eyebrow: 'Esperienze',
      title: 'Su misura,\nsul mare.',
      sub: 'Esperienze tra mare, luce e costa orientale. La Sardegna da vivere con calma.',
      items: [
        {
          tag: 'In barca',
          title: 'Escursioni in barca',
          body: 'Giornate tra Tavolara, acque cristalline e calette raggiungibili solo via mare, lungo la costa orientale della Sardegna.',
        },
        {
          tag: 'In bici',
          title: 'Bicicletta lungo costa',
          body: 'Noleggio biciclette giornaliero per vivere la costa tra mare, tramonti e percorsi panoramici.',
        },
      ],
      request: 'Richiedi esperienza',
    },
    map: {
      eyebrow: 'Posizione',
      title: 'La Caletta,\nSiniscola.',
      body: 'A pochi metri dal mare, nella costa orientale della Sardegna. Vicino a calette nascoste, ristoranti di pesce e i sentieri della macchia mediterranea.',
      address: 'La Caletta · Siniscola (NU) · Sardegna',
    },
    reviews: {
      eyebrow: 'Ospiti',
      title: 'Parole di chi ha vissuto la dimora.',
      items: [
        { quote: 'Luminoso, elegante, rilassante.', author: 'Anna', from: 'Milano' },
        { quote: 'L\'atmosfera della terrazza è bellissima.', author: 'Erik', from: 'Berlino' },
        { quote: 'Posizione perfetta per esplorare la costa.', author: 'Marco', from: 'Bologna' },
        { quote: 'Minimale, silenzioso, raffinato.', author: 'Sophie', from: 'Parigi' },
      ],
    },
    booking: {
      eyebrow: 'Prenota ora',
      title: 'Concedetevi\nun soggiorno.',
      sub: 'Inserisci le date per verificare disponibilità e prezzo in tempo reale.',
      check_in: 'Check-in',
      check_out: 'Check-out',
      guests: 'Ospiti',
      guest_one: 'ospite',
      guest_other: 'ospiti',
      from: 'Da',
      per_night: '/ notte',
      cta: 'Verifica disponibilità',
      note: 'Tariffe indicative. Tassa di soggiorno esclusa.',
      sent: 'Richiesta inviata. Ti risponderemo presto.',
      trust: 'Stripe · Pagamento sicuro',
      conditions_link: 'Condizioni di prenotazione',
      conditions: {
        title: 'Condizioni di prenotazione',
        sections: [
          { h: 'Pagamento', p: 'Tutti i pagamenti vengono elaborati in modo sicuro tramite Stripe.' },
          { h: 'Tassa di soggiorno', p: 'La tassa di soggiorno non è inclusa nel prezzo e dovrà essere corrisposta in loco secondo le disposizioni del Comune di Siniscola.' },
          { h: 'Cancellazione', p: 'La prenotazione non è rimborsabile.' },
          { h: 'Check-in & Check-out', p: 'Check-in disponibile dalle ore 15:00.\nCheck-out entro le ore 10:00.' },
          { h: 'Ospiti', p: "L'appartamento può ospitare un massimo di 5 persone, bambini inclusi." },
          { h: 'Animali', p: 'Animali ammessi su richiesta. Potrebbe essere applicato un supplemento giornaliero.' },
          { h: 'Soggiorno', p: 'Gli ospiti sono invitati a rispettare gli ambienti e la tranquillità della struttura durante il soggiorno.' },
          { h: 'Eventi e fumo', p: "Non sono consentiti feste, eventi o fumo all'interno dell'appartamento." },
          { h: 'Responsabilità', p: 'Eventuali danni arrecati alla struttura o agli arredi durante il soggiorno potranno essere addebitati.' },
        ],
        close: 'Chiudi',
      },
    },
    footer: {
      tagline: 'Una dimora mediterranea\nsulla costa orientale di Sardegna.',
      contact: 'Contatti',
      follow: 'Seguici',
      rights: 'Tutti i diritti riservati.',
    },
    months: ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'],
  },
  en: {
    nav: {
      stay: 'The Spaces',
      gallery: 'Gallery',
      experiences: 'Experiences',
      location: 'Location',
      book: 'Book',
    },
    hero: {
      eyebrow: 'La Caletta · Sardinia',
      title_a: 'La Caletta',
      title_b: "all'Alba",
      sub: 'Sea-view apartment on the eastern coast of Sardinia.',
      cta: 'Discover the apartment',
    },
    intro: {
      eyebrow: 'The Spaces',
      title: 'Light, sea\nand simplicity.',
      body: 'Luminous interiors, essential details and a sea-view terrace designed to live Sardinia slowly — between sunlit days and open-air evenings.',
      details: 'Sea view · 5 guests · 2 bathrooms · private terrace · eastern coast',
      guests: 'guests',
      bedrooms: 'bedrooms',
      bathrooms: 'bathrooms',
      terrace: 'terrace',
    },
    gallery: {
      eyebrow: 'Gallery',
      title: 'Gallery',
      sub: 'Discover every detail of the apartment, designed for comfort, relaxation and a perfect stay in Sardinia.',
      captions: ['Bay View','Open Space Living & Kitchen','Living Area','Equipped Kitchen','Sardinian Flavours','Sofa Bed','Terrace','Mediterranean Details','Master Bedroom','Master Bedroom','Ensuite Bathroom','Twin Bedroom','Modern Bathroom','Stone Details','Private Entrance & Courtyard'],
      coming_soon: 'Coming soon',
    },
    services: {
      eyebrow: 'Amenities',
      title: 'Everything you need,\nnothing superfluous.',
      list: [
        'Sea view',
        'Air conditioning',
        'Fast WiFi',
        'Equipped kitchen',
        'Smart TV',
        'Private terrace',
        'Free parking',
        'Flexible check-in',
        'Coffee corner',
        'Linens included',
        'Washing machine',
        'Private courtyard with outdoor shower',
      ],
    },
    lifestyle: {
      eyebrow: 'Destination',
      title: 'The eastern coast,\nauthentic.',
      body: "Translucent waters, hidden coves and the salt-scented air of true Sardinia. Just steps away, pale beaches, paths perfumed with macchia, and that Mediterranean silence found only here.",
      quote: 'The Sardinian sea is a language: those who listen, understand.',
    },
    experiences: {
      eyebrow: 'Experiences',
      title: 'Tailored,\non the water.',
      sub: 'Experiences between sea, light and the eastern coast. Sardinia, lived slowly.',
      items: [
        {
          tag: 'By boat',
          title: 'Boat excursions',
          body: 'Days between Tavolara, crystal-clear waters and secret coves reachable only from the sea, along the eastern coast of Sardinia.',
        },
        {
          tag: 'By bike',
          title: 'Bicycle along the coast',
          body: 'Daily bicycle rental to live the coast between sea, sunsets and scenic routes.',
        },
      ],
      request: 'Request experience',
    },
    map: {
      eyebrow: 'Location',
      title: 'La Caletta,\nSiniscola.',
      body: 'Steps from the sea, on the eastern coast of Sardinia. Close to hidden coves, seafood restaurants and Mediterranean trails.',
      address: 'La Caletta · Siniscola (NU) · Sardinia',
    },
    reviews: {
      eyebrow: 'Guests',
      title: 'Words from those who lived the stay.',
      items: [
        { quote: 'Bright, elegant and relaxing.', author: 'Anna', from: 'Milan' },
        { quote: 'The terrace atmosphere is beautiful.', author: 'Erik', from: 'Berlin' },
        { quote: 'Perfect location to explore the coast.', author: 'Marco', from: 'Bologna' },
        { quote: 'Minimal, peaceful and refined.', author: 'Sophie', from: 'Paris' },
      ],
    },
    booking: {
      eyebrow: 'Book now',
      title: 'Allow yourself\na stay.',
      sub: 'Select your dates to check live availability and pricing.',
      check_in: 'Check-in',
      check_out: 'Check-out',
      guests: 'Guests',
      guest_one: 'guest',
      guest_other: 'guests',
      from: 'From',
      per_night: '/ night',
      cta: 'Check availability',
      note: 'Indicative rates. City tax not included.',
      sent: 'Request sent. We will reply shortly.',
      trust: 'Stripe · Secure payment',
      conditions_link: 'Booking conditions',
      conditions: {
        title: 'Booking conditions',
        sections: [
          { h: 'Payment', p: 'All payments are securely processed via Stripe.' },
          { h: 'City tax', p: 'City tax is not included in the price and is to be paid on arrival, according to the regulations of the Municipality of Siniscola.' },
          { h: 'Cancellation', p: 'The booking is non-refundable.' },
          { h: 'Check-in & Check-out', p: 'Check-in available from 3:00 pm.\nCheck-out by 10:00 am.' },
          { h: 'Guests', p: 'The apartment accommodates a maximum of 5 people, children included.' },
          { h: 'Pets', p: 'Pets allowed on request. A daily surcharge may apply.' },
          { h: 'Stay', p: 'Guests are kindly asked to respect the apartment and the quiet of the property throughout their stay.' },
          { h: 'Events & smoking', p: 'Parties, events and smoking are not permitted inside the apartment.' },
          { h: 'Liability', p: 'Any damage to the property or furnishings during the stay may be charged to the guest.' },
        ],
        close: 'Close',
      },
    },
    footer: {
      tagline: 'A Mediterranean stay\non the eastern coast of Sardinia.',
      contact: 'Contact',
      follow: 'Follow',
      rights: 'All rights reserved.',
    },
    months: ['January','February','March','April','May','June','July','August','September','October','November','December'],
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState('it');
  const t = useMemo(() => translations[lang], [lang]);
  const toggle = useCallback(() => setLang((l) => (l === 'it' ? 'en' : 'it')), []);
  return (
    <I18nContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
